/**
 * Browser GPU seçimi yalnızca ipucu (`high-performance` | `low-power`).
 * Gerçek cihaz WEBGL_debug_renderer_info / GPUAdapter.info ile loglanır.
 */
export const GPU_POWER_PREFERENCE = "high-performance" as const;

export type ActiveGpuLog = {
  backend: string;
  powerPreference: typeof GPU_POWER_PREFERENCE;
  vendor: string;
  renderer: string;
  [key: string]: unknown;
};

function emitGpuLog(payload: ActiveGpuLog): void {
  // Logger varsayılanı "warn"; müşteri konsolunda her zaman görünsün.
  console.info("[Sugar GPU]", payload);
}

export function logActiveWebGLGpu(
  gl: WebGLRenderingContext | WebGL2RenderingContext,
): void {
  const ext = gl.getExtension("WEBGL_debug_renderer_info") as {
    UNMASKED_VENDOR_WEBGL: number;
    UNMASKED_RENDERER_WEBGL: number;
  } | null;

  const vendor = String(
    (ext
      ? gl.getParameter(ext.UNMASKED_VENDOR_WEBGL)
      : gl.getParameter(gl.VENDOR)) ?? "",
  );
  const renderer = String(
    (ext
      ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER)) ?? "",
  );

  emitGpuLog({
    backend: "webgl",
    context: gl instanceof WebGL2RenderingContext ? "webgl2" : "webgl",
    powerPreference: GPU_POWER_PREFERENCE,
    vendor,
    renderer,
    unmasked: !!ext,
  });
}

type WebGPUAdapterInfo = {
  vendor?: string;
  architecture?: string;
  device?: string;
  description?: string;
};

type WebGPUBackendLike = {
  isWebGPUBackend?: boolean;
  adapter?: {
    info?: WebGPUAdapterInfo;
    name?: string;
  };
};

export function logActiveWebGPUGpu(renderer: {
  backend?: unknown;
  domElement?: HTMLCanvasElement;
}): void {
  const backend = renderer.backend as WebGPUBackendLike | undefined;
  const info = backend?.adapter?.info;
  const vendor = String(info?.vendor ?? "");
  const rendererName = String(
    info?.description || backend?.adapter?.name || "",
  );

  if (!backend?.isWebGPUBackend) {
    const canvas = renderer.domElement;
    const gl =
      canvas?.getContext("webgl2") ??
      canvas?.getContext("webgl");
    if (gl) {
      logActiveWebGLGpu(gl);
      return;
    }
  }

  emitGpuLog({
    backend: backend?.isWebGPUBackend ? "webgpu" : "webgpu-renderer",
    powerPreference: GPU_POWER_PREFERENCE,
    vendor,
    renderer: rendererName,
    architecture: info?.architecture ?? "",
    device: info?.device ?? "",
  });
}
